import type { ClaimItem } from '../../../../types/LegalCase';
import { formatLocalDateToBR, parseLocalDate } from '../../../../utils/DateUtils';

// Status de vencimento do título, derivado na leitura (sem backend). Serve para o jurídico ver
// quando o título venceu e se está pago/vencido/a vencer sem abrir o título — útil sobretudo
// quando a integração com a cobrança estiver ligada e os títulos chegarem por evento.
//
// Precedência: PAGO ganha de tudo (título quitado não "vence"); depois vencido × a vencer pela
// data. Sem data de vencimento, só o rótulo do valor aparece (nenhum badge).

export type DueStatus = 'PAID' | 'OVERDUE' | 'DUE_SOON' | 'UPCOMING';

export interface DueBadge {
    status: DueStatus;
    label: string;
    title: string;
}

// Título quitado: saldo <= 0 com algum pagamento lançado. Alinhado ao ícone verde da linha.
const isPaidOff = (item: ClaimItem): boolean => item.paidAmount > 0 && item.remainingAmount <= 0;

// Dias até o vencimento (negativo = vencido), comparando por data de calendário local. Usa o
// mesmo parse local do rótulo para não sofrer o off-by-one de fuso com data sem hora (YYYY-MM-DD).
const daysUntil = (due: Date, today: Date): number => {
    const startOfDue = Date.UTC(due.getFullYear(), due.getMonth(), due.getDate());
    const startOfToday = Date.UTC(today.getFullYear(), today.getMonth(), today.getDate());
    return Math.round((startOfDue - startOfToday) / 86400000);
};

const DUE_SOON_DAYS = 15;

export const dueBadge = (item: ClaimItem, today: Date = new Date()): DueBadge | null => {
    if (!item.dueDate) return null;
    const formatted = formatLocalDateToBR(item.dueDate);
    if (isPaidOff(item)) {
        return { status: 'PAID', label: 'Pago', title: `Título quitado — vencia em ${formatted}` };
    }
    const due = parseLocalDate(item.dueDate);
    if (!due) return null;
    const days = daysUntil(due, today);
    if (days < 0) {
        return { status: 'OVERDUE', label: `Vencido ${formatted}`, title: `Venceu em ${formatted}` };
    }
    if (days <= DUE_SOON_DAYS) {
        return { status: 'DUE_SOON', label: `Vence ${formatted}`, title: `Vence em ${days} dia(s), em ${formatted}` };
    }
    return { status: 'UPCOMING', label: `Vence ${formatted}`, title: `Vence em ${formatted}` };
};

// Classe Tailwind do badge por status (com dark mode). Fora do componente: puro e testável.
export const dueBadgeClass = (status: DueStatus): string => {
    switch (status) {
        case 'PAID':
            return 'bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300';
        case 'OVERDUE':
            return 'bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-300';
        case 'DUE_SOON':
            return 'bg-amber-100 dark:bg-amber-900 text-amber-700 dark:text-amber-300';
        default:
            return 'bg-gray-200 dark:bg-slate-700 text-gray-600 dark:text-gray-300';
    }
};
